import { INVOICE_STATUS } from 'settings/constants/invoice'
import { RECURRENCE, CONFIGURED_SERVICE_EVENT_TYPE } from 'settings/constants/service'
import { get } from 'utils/lodash'

const { ON_REQUEST } = RECURRENCE
const { INVOICED } = INVOICE_STATUS
const { ONE_OFF, RECURRING } = CONFIGURED_SERVICE_EVENT_TYPE

// Plain pure derivations (NOT createSelector): every caller passes a fresh ad-hoc object, so
// lru(1) memoization could never hit and only thrashed a shared cache across callers — the
// exact anti-pattern CLAUDE.md's Selectors section warns about.
export const getServiceDetails = ({ workOrder }) => ({
  name: get(workOrder, 'serviceName', ''),
  recurrence: get(workOrder, 'recurrence', 'Instance'),
  recurrenceFrequency: get(workOrder, 'recurrenceFrequency', '1'),
  pricingPeriod: get(workOrder, 'pricingPeriod', ''),
  price: get(workOrder, 'servicePrice', 0),
  materialType: get(workOrder, 'materialType', ''),
  measure: get(workOrder, 'measure', ''),
  action: get(workOrder, 'serviceAction', ''),
  method: get(workOrder, 'serviceMethod', ''),
  routeName: get(workOrder, 'routeName', ''),
  driverName: `${get(workOrder, 'driver.firstName', '')} ${get(workOrder, 'driver.lastName', '')}`.trim(),
  serviceDate: get(workOrder, 'serviceDate', ''),
})

export const getWorkOrderData = ({ workOrder }) => ({
  allContainers: get(workOrder, 'containers', []),
  draftFees: get(workOrder, 'draftFees', []),
  invoiceLine: get(workOrder, 'invoiceLine', {}),
  service: { ...getServiceDetails({ workOrder }) },
  workOrderSequence: get(workOrder, 'workOrderSequence', ''),
  workOrderStatus: get(workOrder, 'workOrderStatus', ''),
  invoiceStatus: get(workOrder, 'invoiceStatus', ''),
  invoiceNumber: get(workOrder, 'invoiceNumber', ''),
  invoiceId: get(workOrder, 'invoiceId', ''),
  allNotes: get(workOrder, 'workOrderNotes', []),
  allExceptions: get(workOrder, 'workOrderExceptions', []),
})

export const getInvoiceLineData = ({ invoiceLine }) => ({
  invoiceLineId: get(invoiceLine, 'id', ''),
  invoiceId: get(invoiceLine, 'invoiceId', ''),
  invoiceNumber: get(invoiceLine, 'invoiceNumber', ''),
  invoiceLineItemId: get(invoiceLine, 'invoiceLineItem[0].id', ''),
  invoiceStatus: get(invoiceLine, 'invoiceStatus', ''),
})

export const canChangeWOInvoiceStatusSelector = ({ serviceType, invoiceStatus } = {}) => {
  if (!serviceType || !invoiceStatus) {
    return false
  }
  return serviceType === ON_REQUEST && invoiceStatus !== INVOICED
}

/**
 * Base selector to get the configuredServiceEventType from state
 */
const getConfiguredServiceEventType = state => get(state, 'configuredServiceEventType', '')

/**
 * Determines if a work order or event is of type "One Off"
 * @param {Object} state - The work order or event object
 * @returns {boolean} - True if the work order or event is of type "One Off", false otherwise
 */
export const selectIsOneOff = state => getConfiguredServiceEventType(state)?.toLowerCase() === ONE_OFF

/**
 * Determines if a work order or event is of type "Recurring". Deliberately not `!selectIsOneOff`:
 * crm-core only soft-deletes on an explicit RECURRING event type and hard-deletes everything else,
 * so a missing or unexpected type has to land on the destructive branch here too.
 * @param {Object} state - The work order or event object
 * @returns {boolean} - True if the work order or event is of type "Recurring", false otherwise
 */
export const selectIsRecurringEvent = state => getConfiguredServiceEventType(state)?.toLowerCase() === RECURRING
